import React, { useEffect, useState } from 'react';
import { Document, DocumentSource, listDocumentSources } from '../../../api/documentApi';

// First line of a note, for identifying it in a list
const sourceLine = (html: string): string => {
    const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return text.length > 90 ? `${text.slice(0, 90)}…` : text;
};

interface DocumentToolbarProps {
    documentData: Document | null;
    isEditingTitle: boolean;
    editedTitle: string;
    titleInputRef: React.RefObject<HTMLInputElement | null>;
    isSaving: boolean;
    lastSaved: Date | null;
    showDocInfo: boolean;
    t: any;
    onClose: (e: React.MouseEvent) => void;
    onTitleClick: () => void;
    onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onTitleBlur: () => void;
    onTitleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onToggleDocInfo: () => void;
    onShowVersionHistory: () => void;
    /** Owner only: renaming and sharing are not a collaborator's to do */
    canRename?: boolean;
    onShare?: () => void;
    /** Defaults to the editor's own Share wording when the caller gives none */
    shareLabel?: string;
}

// Removed: a footnote button that opened an empty panel with nothing but a
// Cancel, a convert-to-entry button wired to an empty function, and a Share
// button wired to an empty function. None of the three had anything behind
// them — no footnote support in the schema, no convert endpoint, no sharing.
// Better absent than present and inert.

export const DocumentToolbar: React.FC<DocumentToolbarProps> = ({
    documentData,
    isEditingTitle,
    editedTitle,
    titleInputRef,
    isSaving,
    lastSaved,
    showDocInfo,
    t,
    onClose,
    onTitleClick,
    onTitleChange,
    onTitleBlur,
    onTitleKeyDown,
    onToggleDocInfo,
    onShowVersionHistory,
    canRename = true,
    onShare,
    shareLabel,
}) => {
    const share = shareLabel ?? t.editor.share;
    // Which notes built this document, and whether any of them has been
    // edited since. A document written from a board keeps that trail; one
    // typed from scratch has none, and the section stays hidden.
    const [sources, setSources] = useState<DocumentSource[]>([]);
    const documentId = documentData?.id;

    useEffect(() => {
        if (!showDocInfo || !documentId) return;
        let cancelled = false;
        listDocumentSources(documentId)
            .then((records) => { if (!cancelled) setSources(records); })
            .catch(() => { if (!cancelled) setSources([]); });
        return () => { cancelled = true; };
    }, [showDocInfo, documentId]);

    const diverged = sources.filter((source) => source.has_diverged).length;

    return (
        <div className="document-editor-toolbar">
            <div className="doc-toolbar-left">
                <div className="doc-icon">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#4285F4" />
                        <path d="M14 2V8H20" fill="#A1C2FA" />
                    </svg>
                </div>
                <div className="doc-title-and-status">
                    {isEditingTitle ? (
                        <input
                            ref={titleInputRef}
                            type="text"
                            className="document-title-input"
                            value={editedTitle}
                            onChange={onTitleChange}
                            onBlur={onTitleBlur}
                            onKeyDown={onTitleKeyDown}
                        />
                    ) : (
                        <h1 
                            className="document-title" 
                            onClick={canRename ? onTitleClick : undefined}
                            style={{ cursor: canRename ? 'pointer' : 'default' }}
                        >
                            {documentData?.name}
                        </h1>
                    )}
                    <div className="doc-save-status">
                        {isSaving ? (
                            <span className="saving">{t.editor.saving}</span>
                        ) : lastSaved ? (
                            <span className="saved">
                                {t.editor.saved} {lastSaved.toLocaleTimeString()}
                            </span>
                        ) : (
                            <span className="unsaved">{t.editor.unsaved}</span>
                        )}
                    </div>
                </div>
            </div>
            
            <div className="doc-toolbar-right">
                <button 
                    type="button"
                    onClick={onClose}
                    className="doc-tool-btn secondary"
                    title={t.editor.back}
                >
                    ← {t.editor.back}
                </button>

                {onShare && (
                    <button
                        type="button"
                        onClick={onShare}
                        className="doc-tool-btn"
                        title={share}
                    >
                        👥 {share}
                    </button>
                )}

                <button
                    type="button"
                    onClick={onShowVersionHistory}
                    className="doc-tool-btn"
                    title={t.editor.versionHistory}
                >
                    🕘 {t.editor.versionHistory}
                </button>

                <div className="doc-info-container">
                    <button 
                        type="button"
                        onClick={onToggleDocInfo}
                        className="doc-tool-btn doc-info-btn"
                        title={t.editor.info}
                    >
                        ℹ️ {t.editor.info}
                    </button>
                    
                    {showDocInfo && (
                        <div className="doc-info-panel">
                            <div className="doc-info-section">
                                <h3>{t.editor.info}</h3>
                                <div className="doc-stats">
                                    <div className="stat-item">
                                        <span className="stat-label">{t.editor.infoSourceEntries}</span>
                                        <span className="stat-value">{documentData?.source_entry_count || 0}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">{t.editor.infoTags}</span>
                                        <span className="stat-value">{documentData?.combined_tags?.length || 0}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">{t.editor.infoLinks}</span>
                                        <span className="stat-value">{documentData?.combined_links?.length || 0}</span>
                                    </div>
                                </div>
                            </div>

                            {sources.length > 0 && (
                                <div className="doc-info-section">
                                    <h3>{t.editor.builtFrom}</h3>
                                    {diverged > 0 && (
                                        <p className="doc-sources-diverged">
                                            {t.editor.sourcesChanged}
                                        </p>
                                    )}
                                    <ol className="doc-sources-list">
                                        {sources.map((source) => (
                                            <li key={source.entry_id} className="doc-source">
                                                <span className="doc-source-text">
                                                    {sourceLine(source.original_content)}
                                                </span>
                                                {source.has_diverged && (
                                                    <span
                                                        className="doc-source-badge"
                                                        title={sourceLine(source.current_content)}
                                                    >
                                                        {t.editor.sourceChanged}
                                                    </span>
                                                )}
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            )}

                            {documentData?.combined_tags && documentData.combined_tags.length > 0 && (
                                <div className="doc-info-section">
                                    <h3>{t.editor.tags}</h3>
                                    <div className="doc-tags">
                                        {documentData.combined_tags.map(tag => (
                                            <span
                                                key={tag.id}
                                                className="doc-tag"
                                                style={{ backgroundColor: tag.color }}
                                            >
                                                {tag.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {documentData?.combined_links && documentData.combined_links.length > 0 && (
                                <div className="doc-info-section">
                                    <h3>{t.editor.links}</h3>
                                    <div className="doc-links-list">
                                        {documentData.combined_links.map((link, index) => (
                                            <a
                                                key={index}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="doc-link"
                                            >
                                                🔗 {link.title || link.url}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
